import { Download, Maximize2, Plus, SlidersHorizontal } from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "src/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "src/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "src/components/ui/tabs";
import { cn } from "src/lib/utils";
import type { Schedule, ScheduleRow } from "../utils/amortization";
import { scheduleToCSV } from "../utils/amortization";
import { formatBRL, formatPercent } from "../utils/format";

interface Props {
  baseline: Schedule;
  withExtra: Schedule | null;
  onAddPlan?: () => void;
}

type ColKey =
  | "divida"
  | "correction"
  | "correctionRate"
  | "correctedBalance"
  | "interest"
  | "amortization"
  | "extra"
  | "payment"
  | "balance";

const COLUMNS: { key: ColKey; label: string }[] = [
  { key: "divida", label: "Dívida" },
  { key: "correction", label: "Correção" },
  { key: "correctionRate", label: "Taxa correção" },
  { key: "correctedBalance", label: "Dívida corrigida" },
  { key: "interest", label: "Juros" },
  { key: "amortization", label: "Amort. mensal" },
  { key: "extra", label: "Amortização" },
  { key: "payment", label: "Parcela" },
  { key: "balance", label: "Saldo" },
];

function aggregateAnnual(rows: ScheduleRow[]): ScheduleRow[] {
  const out: ScheduleRow[] = [];
  for (let i = 0; i < rows.length; i += 12) {
    const group = rows.slice(i, i + 12);
    const first = group[0];
    const last = group[group.length - 1];
    const correction = group.reduce((s, r) => s + r.correction, 0);
    out.push({
      month: Math.floor(i / 12) + 1,
      label: `Ano ${Math.floor(i / 12) + 1}`,
      openingBalance: first.openingBalance,
      correctedBalance: first.openingBalance + correction,
      interest: group.reduce((s, r) => s + r.interest, 0),
      amortization: group.reduce((s, r) => s + r.amortization, 0),
      extra: group.reduce((s, r) => s + r.extra, 0),
      correction,
      payment: group.reduce((s, r) => s + r.payment + r.extra, 0),
      balance: last.balance,
    });
  }
  return out;
}

function rateOf(r: ScheduleRow): number {
  return r.openingBalance > 0 ? (r.correction / r.openingBalance) * 100 : 0;
}

function ScheduleTable({
  rows,
  visible,
  hasExtraCol,
  hasCorrection,
  height,
}: {
  rows: ScheduleRow[];
  visible: Record<ColKey, boolean>;
  hasExtraCol: boolean;
  hasCorrection: boolean;
  height: string;
}) {
  const show = (k: ColKey) => {
    if (k === "extra" && !hasExtraCol) return false;
    if ((k === "correction" || k === "correctionRate" || k === "correctedBalance") && !hasCorrection) return false;
    return visible[k];
  };

  return (
    <div className={cn("relative w-full overflow-auto bg-white", height)}>
      <table className="w-full caption-bottom text-sm">
        <TableHeader className="sticky top-0 z-10 bg-white border-b border-border">
          <TableRow>
            <TableHead className="whitespace-nowrap h-10 px-2">Mês</TableHead>
            {show("divida") && <TableHead className="text-right h-10 px-2">Dívida</TableHead>}
            {show("correction") && <TableHead className="text-right h-10 px-2">Correção</TableHead>}
            {show("correctionRate") && <TableHead className="text-right whitespace-nowrap h-10 px-2">Taxa correção</TableHead>}
            {show("correctedBalance") && <TableHead className="text-right whitespace-nowrap h-10 px-2">Dívida corrigida</TableHead>}
            {show("interest") && <TableHead className="text-right h-10 px-2">Juros</TableHead>}
            {show("amortization") && <TableHead className="text-right whitespace-nowrap h-10 px-2">Amort. mensal</TableHead>}
            {show("extra") && <TableHead className="text-right h-10 px-2">Amortização</TableHead>}
            {show("payment") && <TableHead className="text-right h-10 px-2">Parcela</TableHead>}
            {show("balance") && <TableHead className="text-right h-10 px-2">Saldo</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.month}>
              <TableCell className="whitespace-nowrap text-muted-foreground p-2">
                {r.month} | {r.label}
              </TableCell>
              {show("divida") && <TableCell className="text-right tabular-nums p-2">{formatBRL(r.openingBalance)}</TableCell>}
              {show("correction") && <TableCell className="text-right tabular-nums text-muted-foreground p-2">{formatBRL(r.correction)}</TableCell>}
              {show("correctionRate") && <TableCell className="text-right tabular-nums text-muted-foreground p-2">{formatPercent(rateOf(r), 4)}</TableCell>}
              {show("correctedBalance") && <TableCell className="text-right tabular-nums p-2">{formatBRL(r.correctedBalance)}</TableCell>}
              {show("interest") && <TableCell className="text-right tabular-nums p-2">{formatBRL(r.interest)}</TableCell>}
              {show("amortization") && <TableCell className="text-right tabular-nums p-2">{formatBRL(r.amortization)}</TableCell>}
              {show("extra") && (
                <TableCell className="text-right tabular-nums text-[hsl(var(--success))] p-2">
                  {r.extra > 0 ? formatBRL(r.extra) : "—"}
                </TableCell>
              )}
              {show("payment") && <TableCell className="text-right font-medium tabular-nums p-2">{formatBRL(r.payment)}</TableCell>}
              {show("balance") && <TableCell className="text-right tabular-nums p-2">{formatBRL(r.balance)}</TableCell>}
            </TableRow>
          ))}
        </TableBody>
      </table>
    </div>
  );
}

export function AmortizacaoTable({ baseline, withExtra, onAddPlan }: Props) {
  const [tab, setTab] = useState<"baseline" | "extra">("baseline");
  const [period, setPeriod] = useState<"monthly" | "annual">("monthly");
  const [visible, setVisible] = useState<Record<ColKey, boolean>>({
    divida: true,
    correction: true,
    correctionRate: false,
    correctedBalance: true,
    interest: true,
    amortization: true,
    extra: true,
    payment: true,
    balance: true,
  });

  const active = tab === "extra" && withExtra ? withExtra : baseline;
  const hasCorrection = active.totalCorrection > 0.01;
  const hasExtraCol = active.totalExtra > 0.01;

  const rows = useMemo(
    () => (period === "annual" ? aggregateAnnual(active.rows) : active.rows),
    [period, active],
  );

  const download = () => {
    const csv = scheduleToCSV(active);
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `amortizacao-${tab}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const availableCols = COLUMNS.filter((c) => {
    if (c.key === "extra") return hasExtraCol;
    if (c.key === "correction" || c.key === "correctionRate" || c.key === "correctedBalance") return hasCorrection;
    return true;
  });

  const filterControls = (
    <div className="flex flex-wrap items-center gap-2">
      {withExtra && (
        <Tabs value={tab} onValueChange={(v) => setTab(v as "baseline" | "extra")}>
          <TabsList className="h-8 bg-muted w-auto mb-0">
            <TabsTrigger value="baseline" className="text-xs sm:text-sm">Sem amort.</TabsTrigger>
            <TabsTrigger value="extra" className="text-xs sm:text-sm">Com amort.</TabsTrigger>
          </TabsList>
        </Tabs>
      )}
      <Tabs value={period} onValueChange={(v) => setPeriod(v as "monthly" | "annual")} className="bg-muted">
        <TabsList className="h-fit bg-[#f5f0f1] w-auto mb-0 text-[#485e51] font-semibold">
          <TabsTrigger value="monthly" className="text-xs sm:text-sm bg-[#f5f0f1] data-[state=active]:text-black">Mensal</TabsTrigger>
          <TabsTrigger value="annual" className="text-xs sm:text-sm bg-[#f5f0f1] data-[state=active]:text-black">Anual</TabsTrigger>
        </TabsList>
      </Tabs>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm">
            <SlidersHorizontal className="mr-1.5 h-4 w-4" /> Filtro
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <DropdownMenuLabel>Colunas visíveis</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {availableCols.map((c) => (
            <DropdownMenuCheckboxItem
              key={c.key}
              checked={visible[c.key]}
              onCheckedChange={(checked: boolean) => setVisible((prev) => ({ ...prev, [c.key]: !!checked }))}
              onSelect={(e: Event) => e.preventDefault()}
            >
              {c.label}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  return (
    <div className="rounded-xl border border-border bg-card shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-4 sm:p-6">
        <h4 className="text-base font-semibold sm:text-xl">Tabela de amortização</h4>
        <div className="flex flex-wrap items-center gap-2">
          {filterControls}
          <Button variant="outline" size="sm" onClick={download}>
            <Download className="mr-1.5 h-4 w-4" /> CSV
          </Button>
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Maximize2 className="mr-1.5 h-4 w-4" /> Ampliar
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-[98vw] p-4 sm:max-w-[96vw] sm:p-6">
              <DialogHeader>
                <DialogTitle className="text-base sm:text-lg">Tabela de amortização</DialogTitle>
              </DialogHeader>
              <div className="mb-1 flex flex-wrap items-center gap-2">{filterControls}</div>
              <ScheduleTable rows={rows} visible={visible} hasExtraCol={hasExtraCol} hasCorrection={hasCorrection} height="h-[78vh]" />
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <ScheduleTable rows={rows} visible={visible} hasExtraCol={hasExtraCol} hasCorrection={hasCorrection} height="h-[420px]" />

      <div className="flex justify-center border-t border-border p-4 sm:p-6">
        <button
          type="button"
          onClick={onAddPlan}
          className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-gradient-to-br from-[#9c1006] to-[#6e0b04] px-4 py-2.5 text-sm font-medium text-white shadow transition-opacity hover:opacity-90 sm:w-auto"
        >
          <Plus className="h-5 w-5" />
          Adicionar planos de amortização
        </button>
      </div>
    </div>
  );
}
